const CITY_MAX_LENGTH = 100

type AddressComponentLike = {
  long_name?: string | null
  short_name?: string | null
  types?: string[]
}

type GooglePlaceLike = {
  address_components?: AddressComponentLike[] | null
  formatted_address?: string | null
  name?: string | null
}

export type MapLocationSearchEntry = {
  label?: string | null
  door_no?: string | null
  doorNo?: string | null
  street?: string | null
  city?: string | null
  zipcode?: string | null
  lat?: string | number | null
  lon?: string | number | null
}

const cleanText = (value: unknown) => String(value ?? '').trim()

const cleanCityCandidate = (value: unknown) => {
  const text = cleanText(value)
  if (!text || text.length > CITY_MAX_LENGTH) {
    return ''
  }
  return text
}

export const getMapSearchCity = (entry: MapLocationSearchEntry) => {
  const explicitCity = cleanCityCandidate(entry.city)
  if (explicitCity) {
    return explicitCity
  }

  return cleanText(entry.label)
    .split(',')
    .map((part) => cleanCityCandidate(part))
    .find(Boolean) ?? ''
}

export const getGooglePlaceCity = (place: GooglePlaceLike | null | undefined) => {
  const components = place?.address_components ?? []
  const cityTypes = [
    'locality',
    'postal_town',
    'administrative_area_level_3',
    'sublocality_level_1',
    'administrative_area_level_2',
  ]

  for (const cityType of cityTypes) {
    const component = components.find((item) => item.types?.includes(cityType))
    const city = cleanCityCandidate(component?.long_name ?? component?.short_name)
    if (city) {
      return city
    }
  }

  return ''
}

export const getMapSearchAddress = (entry: MapLocationSearchEntry) => {
  const address = [
    entry.door_no ?? entry.doorNo,
    entry.street,
    getMapSearchCity(entry),
    entry.zipcode,
  ]
    .map(cleanText)
    .filter(Boolean)
    .join(', ')

  return address || cleanText(entry.label)
}

export const buildMapSearchDetails = (entry: MapLocationSearchEntry) => {
  const address = getMapSearchAddress(entry)
  const coordinates = [entry.lat, entry.lon]
    .map(cleanText)
    .filter(Boolean)
    .join(', ')

  return [address, coordinates].filter(Boolean).join(' | ')
}
